import React, { useState, useEffect, useCallback } from "react";
import {
  DatabaseBackup, CheckCircle2, XCircle, Clock, RotateCcw, ShieldCheck, Landmark,
} from "lucide-react";
import { Card, Button, Badge, Modal, Spinner } from "../lib/ui";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const TYPE_LABEL = {
  daily: "Harian", weekly: "Mingguan", monthly: "Bulanan", fresh_start: "Baseline Produksi",
  manual: "Manual", pre_restore: "Pra-Restore", pre_upgrade: "Pra-Upgrade",
};

const fmtSize = (b) => {
  if (!b) return "—";
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
};
const fmtTime = (iso) => (iso || "").slice(0, 16).replace("T", " ");

function VerifBadge({ v }) {
  if (v === "verified") return <Badge tone="success"><CheckCircle2 size={12} /> Terverifikasi</Badge>;
  if (v === "failed") return <Badge tone="danger"><XCircle size={12} /> Gagal</Badge>;
  return <Badge tone="warn">—</Badge>;
}

function Row({ label, children, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 text-sm" data-testid={testid}>
      <span className="text-ink-soft">{label}</span>
      <span className="text-right font-semibold text-ink">{children}</span>
    </div>
  );
}

function RestoreModal({ open, onClose, toast, onDone }) {
  const [items, setItems] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/integrity/backups"); setItems(data.backups); }
    catch { toast.error("Gagal memuat daftar backup"); }
  }, [toast]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const doRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrity/backup/${confirmFor.id}/restore`, { confirm: confirmText });
      toast.success(data.ok ? `Restore selesai dari ${data.backup_name} (safety: ${data.safety_backup})` : "Restore selesai dengan selisih — periksa audit");
      setConfirmFor(null); setConfirmText("");
      onDone?.(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore gagal"); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Modal open onClose={busy ? () => {} : onClose} title="Kelola & Restore Backup" size="lg">
      <div data-testid="backup-list-modal">
        {!items ? <div className="flex h-32 items-center justify-center"><Spinner className="h-6 w-6" /></div> : (
          <div className="max-h-[55vh] space-y-2 overflow-y-auto pr-1">
            {items.length === 0 && <p className="py-6 text-center text-sm text-ink-mute">Belum ada backup.</p>}
            {items.map((b) => (
              <div key={b.id} className={`rounded-xl border px-3.5 py-2.5 ${b.status === "expired" ? "border-line opacity-50" : "border-line"}`} data-testid={`backup-row-${b.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="flex items-center gap-1.5 font-mono text-xs font-bold text-ink">
                      {b.baseline && <Landmark size={13} className="text-amber-600" />}
                      {b.protected && !b.baseline && <ShieldCheck size={13} className="text-emerald-600" />}
                      {b.name}
                    </p>
                    <p className="text-[11px] text-ink-mute">
                      {TYPE_LABEL[b.type] || b.type} · {fmtTime(b.created_at)} · {fmtSize(b.size_bytes)} · {Number(b.total_docs || 0).toLocaleString("id-ID")} dok · oleh {b.created_by}
                      {b.baseline && <b className="text-amber-700"> · BASELINE PRODUKSI (permanen)</b>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {b.status === "expired" ? <Badge tone="warn">Kedaluwarsa</Badge> : <VerifBadge v={b.verification} />}
                    {b.status === "completed" && b.verification === "verified" && (
                      <Button size="sm" variant="outline" onClick={() => { setConfirmFor(b); setConfirmText(""); }} data-testid={`backup-restore-${b.id}`}>
                        <RotateCcw size={13} /> Restore
                      </Button>
                    )}
                  </div>
                </div>
                {confirmFor?.id === b.id && (
                  <div className="mt-2 rounded-lg bg-danger-light p-3" data-testid="restore-confirm-box">
                    <p className="mb-2 text-xs text-danger-text">
                      Seluruh data akan dikembalikan ke kondisi backup ini. Safety backup otomatis dibuat sebelum restore.
                      Ketik <b>RESTORE</b> untuk konfirmasi:
                    </p>
                    <div className="flex gap-2">
                      <input className="w-full rounded-lg border border-line px-2.5 py-1.5 text-sm" value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)} placeholder="RESTORE" data-testid="restore-confirm-input" />
                      <Button size="sm" variant="danger" onClick={doRestore} loading={busy} disabled={confirmText !== "RESTORE"} data-testid="restore-confirm-btn">Jalankan</Button>
                      <Button size="sm" variant="outline" onClick={() => setConfirmFor(null)} disabled={busy}>Batal</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default function BackupCenter({ onRestored }) {
  const toast = useToast();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/integrity/backup/status"); setS(data); }
    catch { /* non-owner or transient */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const backupNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/integrity/backup/run"); toast.success(`Backup ${data.name} terverifikasi ✓`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup gagal"); }
    finally { setBusy(false); }
  };

  const toggleScheduler = async () => {
    try { await api.put("/integrity/backup/settings", { scheduler_enabled: !s.scheduler_enabled }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah pengaturan"); }
  };

  const changeTime = async (e) => {
    const v = e.target.value;
    if (!v) return;
    try { await api.put("/integrity/backup/settings", { daily_time: v }); toast.success(`Jadwal backup harian: ${v} WIB`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal mengubah jam"); }
  };

  if (!s) return null;
  const last = s.last_backup;

  return (
    <Card className="mb-5" data-testid="backup-center">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-ink">
          <DatabaseBackup size={18} /> Pusat Backup Produksi
        </h3>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={backupNow} loading={busy} data-testid="backup-now-btn"><DatabaseBackup size={13} /> Backup Sekarang</Button>
          <Button size="sm" variant="outline" onClick={() => setShowList(true)} data-testid="backup-manage-btn"><RotateCcw size={13} /> Restore</Button>
        </div>
      </div>
      <div className="grid gap-x-8 sm:grid-cols-2">
        <Row label="Backup Terakhir" testid="backup-last">
          {last ? <span className="font-mono text-xs">{last.name}</span> : "Belum ada"}
        </Row>
        <Row label="Status" testid="backup-last-status">
          {last ? (last.status === "completed" ? <Badge tone="success">Berhasil</Badge> : <Badge tone="danger">{last.status}</Badge>) : "—"}
        </Row>
        <Row label="Verifikasi" testid="backup-last-verif"><VerifBadge v={last?.verification} /></Row>
        <Row label="Backup Berikutnya" testid="backup-next">
          <span className="flex items-center justify-end gap-1 text-xs"><Clock size={12} /> {s.scheduler_enabled ? s.next_scheduled?.daily : "Scheduler nonaktif"}</span>
        </Row>
        <Row label="Tes Restore Terakhir" testid="backup-last-restore">
          {s.last_restore ? (
            <span className="text-xs">{fmtTime(s.last_restore.at)} {s.last_restore.ok ? <Badge tone="success">OK</Badge> : <Badge tone="danger">Selisih</Badge>}</span>
          ) : "Belum pernah"}
        </Row>
        <Row label="Scheduler Otomatis" testid="backup-scheduler">
          <span className="flex items-center justify-end gap-2">
            <input type="time" className="rounded-lg border border-line px-1.5 py-0.5 text-xs" defaultValue={s.daily_time} onBlur={changeTime} data-testid="backup-time-input" />
            <button onClick={toggleScheduler} data-testid="backup-scheduler-toggle"
              className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.scheduler_enabled ? "bg-success-light text-success-text" : "bg-danger-light text-danger-text"}`}>
              {s.scheduler_enabled ? "AKTIF · WIB" : "NONAKTIF"}
            </button>
          </span>
        </Row>
      </div>
      {(!s.destination_ok || !s.storage_ok) && (
        <p className="mt-2 rounded-lg bg-danger-light px-3 py-1.5 text-xs text-danger-text" data-testid="backup-storage-warning">
          ⚠ {!s.destination_ok ? "Destinasi backup tidak tersedia." : "Ruang penyimpanan kritis — backup baru dihentikan, backup terproteksi aman."}
        </p>
      )}
      {s.baseline && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-ink-mute" data-testid="backup-baseline">
          <Landmark size={12} className="text-amber-600" /> Baseline Produksi: <b className="font-mono">{s.baseline.name}</b> (permanen, tidak pernah dihapus otomatis)
        </p>
      )}
      <RestoreModal open={showList} onClose={() => setShowList(false)} toast={toast} onDone={() => { load(); onRestored?.(); }} />
    </Card>
  );
}
